import { FC, useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useForm, useField } from 'react-final-form';
import { IPage } from 'store/pages/types';
import {
  CUSTOM_ALLOCATION_TYPES,
  DONATION_ALLOCATION_LABELS,
  DONATION_ALLOCATION_OPTIONS,
} from './constants';
import { useFieldValue } from 'hooks/useFormHelpers';
import { RadioField } from 'components/Forms/RadioField';
import { WeightedAllocationTable } from '../WeightedAllocationTable';
import { WaterfallAllocationTable } from '../WaterfallAllocationTable';
import { ConfirmationModal } from 'components/Modal/confirmationModal';
import { AllocationGroup } from '../types';

type Props = {
  isPreviewVisible: boolean;
  editPageSelected?: IPage;
  isPageDuplicated?: boolean;
  onEditRecipients?: () => void;
};

export const DonationAllocationForm: FC<Props> = ({
  isPreviewVisible,
  editPageSelected,
  isPageDuplicated,
  onEditRecipients,
}) => {
  const { change } = useForm();
  const allocationType = useFieldValue('allocation_type');
  const recurringEnabled = useFieldValue('recurring_enabled');
  const pageType = useFieldValue('page_type');
  const recipientGroups: AllocationGroup[] = useFieldValue('recipient_groups') || [];
  const recipientsSelected: { value: string; label: string }[] =
    useFieldValue('recipients_selected') || [];

  const {
    meta: { error, submitFailed },
  } = useField('recipient_groups', { subscription: { error: true, submitFailed: true } });

  const [pendingType, setPendingType] = useState<string | null>(null);
  const [pendingPageType, setPendingPageType] = useState<string | null>(null);
  const skipPageTypeEffect = useRef(false);

  const totalWeight = recipientGroups.reduce((sum, g) => sum + (g.weight_percent || 0), 0);
  const allocationRemaining = 100 - totalWeight;

  const recurringLocked = !!editPageSelected && !isPageDuplicated && !!recurringEnabled;
  const isCustomType = CUSTOM_ALLOCATION_TYPES.includes(allocationType);

  useEffect(() => {
    if (skipPageTypeEffect.current) {
      skipPageTypeEffect.current = false;
      return;
    }
    if (pageType !== 'COMPACT') {
      if (isCustomType && recipientGroups.length > 0) {
        skipPageTypeEffect.current = true;
        change('page_type', 'COMPACT');
        setPendingPageType(pageType);
      } else {
        change('allocation_type', 'EVEN');
      }
    }
  }, [pageType]);

  if (recipientsSelected.length === 0) return null;

  const handleAllocationTypeChange = (value: string | boolean) => {
    const newType = value as string;
    const isCustomSwitch =
      CUSTOM_ALLOCATION_TYPES.includes(allocationType) && allocationType !== newType;

    if (isCustomSwitch && recipientGroups.length > 0) {
      setPendingType(newType);
    } else {
      change('allocation_type', newType);
    }
  };

  const handleModeSwitchConfirm = () => {
    if (!pendingType) return;
    change(
      'recipient_groups',
      recipientGroups.map(g => ({ ...g, weight_percent: 0, donation_limit_cents: null }))
    );
    change('allocation_type', pendingType);
    setPendingType(null);
  };

  const handleModeSwitchCancel = () => setPendingType(null);

  const handlePageTypeSwitchConfirm = () => {
    if (!pendingPageType) return;
    skipPageTypeEffect.current = true;
    change('page_type', pendingPageType);
    change('recipient_groups', []);
    change('allocation_type', 'EVEN');
    setPendingPageType(null);
  };

  const handlePageTypeSwitchCancel = () => setPendingPageType(null);

  return (
    <div className="form-group">
      <ConfirmationModal
        isOpen={!!pendingType}
        handleSave={handleModeSwitchConfirm}
        handleCancel={handleModeSwitchCancel}
        warningMessage="Switching allocation types will clear configured amounts but preserve recipient groups. Do you want to proceed?"
        confirmButtonText="Yes, switch"
      />
      <ConfirmationModal
        isOpen={!!pendingPageType}
        handleSave={handlePageTypeSwitchConfirm}
        handleCancel={handlePageTypeSwitchCancel}
        warningMessage="Changing the page type will clear your custom allocation configuration. Do you want to proceed?"
        confirmButtonText="Yes, change"
      />
      <span className="oath-eyebrow inkBlue">Donation allocation options</span>
      <div className="row align-center space-between">
        <div
          className={classNames('padding0', {
            'col-md-6': !isPreviewVisible,
            'col-md-12': isPreviewVisible,
          })}
        >
          <RadioField
            name="allocation_type"
            options={DONATION_ALLOCATION_OPTIONS}
            label={DONATION_ALLOCATION_LABELS.find(label => label.value === allocationType)?.label}
            bottomLabel={true}
            disabledValues={
              recurringLocked || pageType !== 'COMPACT' ? CUSTOM_ALLOCATION_TYPES : undefined
            }
            onChangeValue={handleAllocationTypeChange}
          />
          {recurringLocked && (
            <p className="oath-utility gray">
              Custom allocation types are not available on pages with recurring payments enabled.
            </p>
          )}
          {pageType !== 'COMPACT' && (
            <p className="oath-utility gray">
              Custom allocation types are only available on Compact pages.
            </p>
          )}
        </div>
        {allocationType === 'WEIGHTED' && (
          <div className="weighted-allocation-meta">
            <span className="oath-utility gray">Allocation Remaining</span>
            <div
              className={classNames('allocation-remaining-value', {
                'allocation-remaining-value--zero': allocationRemaining === 0,
              })}
            >
              <span
                className={classNames('oath-bodysmall', {
                  errorRed: allocationRemaining !== 0,
                  inkBlue: allocationRemaining === 0,
                })}
              >
                {allocationRemaining}%
              </span>
            </div>
            {onEditRecipients && (
              <button
                type="button"
                className="link-text oath-bodysmall gray padding0"
                onClick={onEditRecipients}
              >
                Edit Recipients
              </button>
            )}
          </div>
        )}
      </div>
      {isCustomType && submitFailed && error && (
        <p className="oath-utility errorRed allocation-error">{error}</p>
      )}
      {allocationType === 'WEIGHTED' && <WeightedAllocationTable />}
      {allocationType === 'WATERFALL' && <WaterfallAllocationTable />}
    </div>
  );
};
